import axios from 'axios';
import { accountingApi } from '../config/apiClient';
import { XMLParser } from 'fast-xml-parser';

export interface CurrencyRate {
  code: string;        // Döviz kodu (USD, EUR, GBP vb.)
  name: string;        // Döviz adı
  unit: number;        // Birim
  buying: number;      // Alış kuru
  selling: number;     // Satış kuru
  effectiveDate: Date; // Geçerlilik tarihi
}

class CurrencyService {
  // Singleton yapısı
  private static instance: CurrencyService;
  private parser: XMLParser;
  
  private constructor() {
    this.parser = new XMLParser({
      ignoreAttributes: false,
      attributeNamePrefix: "@_"
    });
  }
  
  public static getInstance(): CurrencyService {
    if (!CurrencyService.instance) {
      CurrencyService.instance = new CurrencyService();
    }
    return CurrencyService.instance;
  }
  
  /**
   * TCMB'den güncel döviz kurlarını çeker
   * @returns Döviz kurları listesi
   */
  public async fetchExchangeRates(): Promise<CurrencyRate[]> {
    try {
      console.log('📊 TCMB döviz kurları backend proxy üzerinden çekiliyor...');
      
      // Backend proxy endpoint'ini kullan (CORS sorunu yok)
      const backendProxyUrl = process.env.REACT_APP_ACCOUNTINGAPI_URL || 'http://localhost:5237';
      const response = await accountingApi.get(`${backendProxyUrl}/api/Currency/tcmb-rates`);
      
      console.log('✅ Backend proxy üzerinden TCMB yanıtı alındı');
      
      // XML'i parse et
      const parsedData = this.parser.parse(response.data);
      
      // Kurlar dizisini al
      const currencyList = parsedData.Tarih_Date.Currency;
      
      // Efektif tarih
      const effectiveDate = new Date(
        parsedData.Tarih_Date["@_Date"].split('.').reverse().join('-')
      );
      
      // Kurları dönüştür
      const rates: CurrencyRate[] = currencyList.map((item: any) => {
        // Bazı dövizlerde efektif kur olmayabilir, o durumda döviz kuru kullanılır
        const banknoteBuying = parseFloat(item.BanknoteBuying) || 0;
        const banknoteSelling = parseFloat(item.BanknoteSelling) || 0;
        const forexBuying = parseFloat(item.ForexBuying) || 0;
        const forexSelling = parseFloat(item.ForexSelling) || 0;
        
        return {
          code: item["@_Kod"],
          name: item.Isim,
          unit: parseInt(item.Unit),
          // Efektif kur varsa onu kullan, yoksa döviz kurunu kullan
          buying: banknoteBuying > 0 ? banknoteBuying : forexBuying,
          selling: banknoteSelling > 0 ? banknoteSelling : forexSelling,
          effectiveDate
        };
      });
      
      console.log(`✅ ${rates.length} döviz kuru başarıyla parse edildi`);
      return rates;
    } catch (error) {
      console.error('❌ Döviz kurları çekilirken hata oluştu:', error);
      
      // Hata detaylarını görelim
      if (axios.isAxiosError(error)) {
        console.error('Axios hatası:', error.message);
        console.error('Hata detayı:', error.response?.data);
        console.error('Hata durumu:', error.response?.status);
      }
      
      // Mock data ile test edelim - Güncel kurlar kullanılıyor
      console.warn('⚠️ Mock döviz kurları kullanılıyor (Backend\'e bağlanılamadı)');
      return this.getMockCurrencyRates();
    }
  }
  
  /**
   * Test için sabit döviz kurları - 08.07.2024 tarihli TCMB kurları
   * @returns Mock döviz kurları
   */
  private getMockCurrencyRates(): CurrencyRate[] {
    return [
      {
        code: 'USD',
        name: 'ABD DOLARI',
        unit: 1,
        buying: 38.1032, // Efektif alış kuru genellikle daha düşüktür
        selling: 38.2716, // Efektif satış kuru genellikle daha yüksektir
        effectiveDate: new Date()
      },
      {
        code: 'AUD',
        name: 'AVUSTRALYA DOLARI',
        unit: 1,
        buying: 25.4031,
        selling: 25.7845,
        effectiveDate: new Date()
      },
      {
        code: 'DKK',
        name: 'DANİMARKA KRONU',
        unit: 1,
        buying: 5.5017,
        selling: 5.6275,
        effectiveDate: new Date()
      },
      {
        code: 'EUR',
        name: 'EURO',
        unit: 1,
        buying: 41.2853,
        selling: 41.5605,
        effectiveDate: new Date()
      },
      {
        code: 'GBP',
        name: 'İNGİLİZ STERLİNİ',
        unit: 1,
        buying: 48.7276,
        selling: 49.1546,
        effectiveDate: new Date()
      },
      {
        code: 'CHF',
        name: 'İSVİÇRE FRANGI',
        unit: 1,
        buying: 42.6293,
        selling: 43.1083,
        effectiveDate: new Date()
      },
      {
        code: 'SEK',
        name: 'İSVEÇ KRONU',
        unit: 1,
        buying: 3.5391,
        selling: 3.6768,
        effectiveDate: new Date()
      },
      {
        code: 'CAD',
        name: 'KANADA DOLARI',
        unit: 1,
        buying: 27.6631,
        selling: 27.9820,
        effectiveDate: new Date()
      },
      {
        code: 'KWD',
        name: 'KUVEYT DİNARI',
        unit: 1,
        buying: 123.4906,
        selling: 125.0399,
        effectiveDate: new Date()
      },
      {
        code: 'NOK',
        name: 'NORVEÇ KRONU',
        unit: 1,
        buying: 3.4991,
        selling: 3.6242,
        effectiveDate: new Date()
      },
      {
        code: 'SAR',
        name: 'SUUDİ ARABİSTAN RİYALİ',
        unit: 1,
        buying: 10.0812,
        selling: 10.2000,
        effectiveDate: new Date()
      },
      {
        code: 'JPY',
        name: 'JAPON YENİ',
        unit: 100,
        buying: 23.5486,
        selling: 23.8916,
        effectiveDate: new Date()
      },
      {
        code: 'BGN',
        name: 'BULGAR LEVASI',
        unit: 1,
        buying: 21.0457,
        selling: 21.4749,
        effectiveDate: new Date()
      },
      {
        code: 'RON',
        name: 'RUMEN LEYİ',
        unit: 1,
        buying: 8.2075,
        selling: 8.4967,
        effectiveDate: new Date()
      },
      {
        code: 'RUB',
        name: 'RUS RUBLESİ',
        unit: 1,
        buying: 0.4096,
        selling: 0.4341,
        effectiveDate: new Date()
      },
      {
        code: 'IRR',
        name: 'İRAN RİYALİ',
        unit: 100,
        buying: 0.0869,
        selling: 0.0908,
        effectiveDate: new Date()
      },
      {
        code: 'CNY',
        name: 'ÇİN YUANI',
        unit: 1,
        buying: 5.1397,
        selling: 5.3943,
        effectiveDate: new Date()
      },
      {
        code: 'PKR',
        name: 'PAKİSTAN RUPİSİ',
        unit: 1,
        buying: 0.1334,
        selling: 0.1388,
        effectiveDate: new Date()
      }
    ];
  }
  
  /**
   * Belirli bir döviz kurunu getirir
   * @param currencyCode Döviz kodu (USD, EUR, GBP vb.)
   * @returns İlgili döviz kuru veya bulunamazsa null
   */
  public async getCurrencyRate(currencyCode: string): Promise<CurrencyRate | null> {
    try {
      const rates = await this.fetchExchangeRates();
      const rate = rates.find(r => r.code === currencyCode);
      return rate || null;
    } catch (error) {
      console.error(`${currencyCode} kuru çekilirken hata:`, error);
      return null;
    }
  }
  
  /**
   * Para birimi sembolünü döndürür
   * @param currencyCode Döviz kodu
   * @returns Para birimi sembolü
   */
  public getCurrencySymbol(currencyCode: string): string {
    switch(currencyCode) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      default: return currencyCode;
    }
  }
}

export default CurrencyService.getInstance(); 